import { FC, useMemo } from 'react';

import {
  Container,
  Heading,
  Link,
  RatingStats,
  Sharing,
  StrainInfo,
} from '@/components';
import { NOT_DEFINED } from '@/constants';
import { useFeaturedFlag, useIsMobile } from '@/hooks';
import { CustomerReview, GProduct, Store } from '@/types';

import { useProductContext } from '../Product/context';
import { AboutProduct } from './AboutProduct';
import { FilteredTabReviews } from './FilteredTabReviews';
import { LabResults } from './LabResults';
import { ProductBadges } from './ProductBadges';
import { NewProductBogo } from './ProductBogo';
import { ProductControlsPortal } from './ProductControlPortal';
import { ProductControls } from './ProductControls';
import { ProductDescription } from './ProductDescription';
import { ProductExtraDescriptions } from './ProductExtraDescriptions';
import { ProductFeats } from './ProductFeats';
import { ProductImages } from './ProductImages';
import { ProductReviewsFilters } from './ProductReviewsFilters';
import { ProductWarning } from './ProductWarning';
import { ReviewsFilterProvider } from './ReviewsFilterProvider';

interface IProductWithUrls extends GProduct {
  brand_url: string;
  category_url: string;
}

interface IProductDetailProps {
  product: IProductWithUrls;
  reviews: CustomerReview[];
  store: Store;
  isProductAvailable?: boolean;
}

const ProductDetail: FC<IProductDetailProps> = ({
  product,
  reviews,
  store,
  isProductAvailable,
}) => {
  const { isMobile } = useIsMobile();
  const { disableProductReviews } = useFeaturedFlag();
  const { isSpecial, weightVariant, compoundList } = useProductContext();

  const brandLink = (
    <Link
      aria-label={`Go to ${product.brand_name}`}
      className="details__link"
      href={product.brand_url}
    >
      {product.brand_name || NOT_DEFINED}
    </Link>
  );

  const productControlsProps = useMemo(
    () => ({
      isProductAvailable,
      isSpecial,
      product,
      weightVariant,
    }),
    [isProductAvailable, isSpecial, product, weightVariant],
  );

  return (
    <ReviewsFilterProvider reviews={reviews}>
      <section className={'details'}>
        <Container>
          <div className={'container'}>
            <ProductImages product={product} />
            <div className={'details__body'}>
              <NewProductBogo product={product} />
              <ProductBadges product={product} weightVariant={weightVariant} />
              <span className={'details__span'}>
                {!product?.brand_name && NOT_DEFINED}
                {product?.brand_name && brandLink}
              </span>
              <Heading className="details__product_heading" level={1}>
                {product.name}
              </Heading>
              {product?.flower_type ? (
                <StrainInfo
                  className="flower__type"
                  strain={product?.flower_type}
                />
              ) : null}
              <ProductFeats compoundList={compoundList} product={product} />
              <ProductDescription product={product} />
              <ProductExtraDescriptions product={product} />
              <ProductWarning product={product} />
              {!isMobile && <ProductControls {...productControlsProps} />}
              <AboutProduct compoundList={compoundList} product={product} />
              <Heading className={'details__subtitle'} level={4}>
                Share
              </Heading>
              <Sharing
                className={'sharing'}
                include={['facebook', 'twitter', 'pinterest', 'whatsapp']}
                media={product.asset?.image as string}
                url={`${store.domain}${product.url}`}
              />
            </div>
          </div>
          <LabResults labResults={product.labResults} />
          {reviews && !disableProductReviews && (
            <div className="details__rating-reviews">
              <RatingStats
                className={'rating__component'}
                review_rating={product?.review_rating || 0}
                reviews={reviews || []}
                total_reviews={product?.reviews || 0}
              />
              <ProductReviewsFilters reviews={reviews} />
            </div>
          )}
        </Container>
        {!disableProductReviews && <FilteredTabReviews />}
      </section>
      {/* Only render on mobile using the wrapper component */}
      <ProductControlsPortal {...productControlsProps} />
    </ReviewsFilterProvider>
  );
};

export { ProductDetail };
